import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Paper,
  Grid,
  alpha,
  useTheme
} from '@mui/material';
import {
  Info as InfoIcon,
  LocationOn as LocationIcon,
  Label as LabelIcon,
  Visibility as VisibilityIcon,
  CalendarToday as CalendarIcon
} from '@mui/icons-material';
import { AnnotationDetails } from '../../../../services/abcdeAPI';

interface AnnotationInfoPanelProps {
  annotationId: string;
  annotationDetails: AnnotationDetails | null;
  loading?: boolean;
}

const AnnotationInfoPanel: React.FC<AnnotationInfoPanelProps> = ({
  annotationId,
  annotationDetails,
  loading = false
}) => {
  const theme = useTheme();

  if (!annotationDetails && !loading) {
    return null;
  }

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <InfoIcon color="primary" />
          Annotation Information
        </Typography>
        
        {loading ? (
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Loading annotation details...
            </Typography>
          </Box>
        ) : annotationDetails ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Basic Information */}
            <Paper elevation={0} sx={{ p: 2, bgcolor: alpha(theme.palette.primary.main, 0.05), borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LabelIcon fontSize="small" color="primary" />
                Basic Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    <strong>Annotation ID:</strong>
                  </Typography>
                  <Typography variant="body2">{annotationId}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    <strong>Label:</strong>
                  </Typography>
                  <Typography variant="body2">{annotationDetails.label}</Typography>
                </Grid>
              </Grid>
            </Paper>

            {/* Position Information */}
            <Paper elevation={0} sx={{ p: 2, bgcolor: alpha(theme.palette.secondary.main, 0.05), borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocationIcon fontSize="small" color="secondary" />
                Position & Coordinates
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    <strong>X Position:</strong>
                  </Typography>
                  <Chip 
                    label={`${Math.round(annotationDetails.x * 100)}%`} 
                    size="small" 
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    <strong>Y Position:</strong>
                  </Typography>
                  <Chip 
                    label={`${Math.round(annotationDetails.y * 100)}%`} 
                    size="small" 
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    <strong>Precise Coordinates:</strong>
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    ({annotationDetails.x.toFixed(6)}, {annotationDetails.y.toFixed(6)})
                  </Typography>
                </Grid>
              </Grid>
            </Paper>

            {/* Confidence & Quality */}
            {annotationDetails.confidence !== undefined && annotationDetails.confidence !== null && (
              <Paper elevation={0} sx={{ p: 2, bgcolor: alpha(theme.palette.info.main, 0.05), borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <VisibilityIcon fontSize="small" color="info" />
                  Detection Quality
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    <strong>Confidence Score:</strong>
                  </Typography>
                  <Chip 
                    label={`${Math.round(annotationDetails.confidence * 100)}%`}
                    color={annotationDetails.confidence > 0.8 ? 'success' : annotationDetails.confidence > 0.6 ? 'warning' : 'error'}
                    variant="filled"
                    size="small"
                  />
                  <Typography variant="caption" color="text.secondary">
                    {annotationDetails.confidence > 0.8 ? 'High confidence' : 
                     annotationDetails.confidence > 0.6 ? 'Moderate confidence' : 
                     'Low confidence'}
                  </Typography>
                </Box>
              </Paper>
            )}

            {/* Image Information */}
            <Paper elevation={0} sx={{ p: 2, bgcolor: alpha(theme.palette.warning.main, 0.05), borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarIcon fontSize="small" color="warning" />
                Image Details
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    <strong>Image Type:</strong>
                  </Typography>
                  <Chip 
                    label={annotationDetails.linkedMicroUrl ? 'Microscopic Image' : 'Macroscopic Image'}
                    color={annotationDetails.linkedMicroUrl ? 'primary' : 'secondary'}
                    variant="outlined"
                    size="small"
                  />
                </Grid>
              </Grid>
            </Paper>

            {/* Technical Details */}
            <Paper elevation={0} sx={{ p: 2, bgcolor: alpha(theme.palette.grey[500], 0.05), borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Technical Metadata
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  <strong>Macro Image URL:</strong> {annotationDetails.macroImageUrl || 'Not available'}
                </Typography>
                {annotationDetails.linkedMicroUrl && (
                  <Typography variant="caption" color="text.secondary">
                    <strong>Micro Image URL:</strong> {annotationDetails.linkedMicroUrl}
                  </Typography>
                )}
              </Box>
            </Paper>
          </Box>
        ) : (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No annotation details available
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default AnnotationInfoPanel;